import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TechnicianService from "../services/technician.service";
import type { Technician } from "../pages/models";

export interface LeaveItem {
  id: string;
  technician: string; // technician ID
  technicianName?: string;
  technician_name?: string;
  start_date_time: string;
  end_date_time: string;
  is_all_day: boolean;
  isAllDay?: boolean;
  startDateTime?: string;
  endDateTime?: string;
}

type LeaveModalProps = {
  show: boolean;
  onHide: () => void;
  leave?: LeaveItem | null; // If provided, we are in Edit/Delete mode
  defaultStartDate?: Date | null;
  onLeaveChanged: () => void;
  filterType?: "dyno" | "service";
};

type LeaveType = "days" | "hours";

const LeaveModal = ({
  show,
  onHide,
  leave,
  defaultStartDate,
  onLeaveChanged,
  filterType,
}: LeaveModalProps) => {
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [leaveType, setLeaveType] = useState<LeaveType>("days");
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");
  const [selectedTechIds, setSelectedTechIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch technicians
  useEffect(() => {
    if (show) {
      TechnicianService.getTechnicians()
        .then((res) => {
          let list = res.data.data || [];
          if (filterType === "dyno") {
            list = list.filter((t: any) => t.supports_dyno !== false && t.supportsDyno !== false);
          } else if (filterType === "service") {
            list = list.filter((t: any) => t.supports_service !== false && t.supportsService !== false);
          }
          setTechnicians(list);
        })
        .catch((err) => {
          console.error("Error fetching technicians", err);
        });
    }
  }, [show, filterType]);

  // Set initial form state based on leave (edit mode) or defaults (add mode)
  useEffect(() => {
    if (show) {
      if (leave) {
        const startStr = leave.start_date_time || leave.startDateTime;
        const endStr = leave.end_date_time || leave.endDateTime;
        const parsedStart = startStr ? new Date(startStr) : new Date();
        const parsedEnd = endStr ? new Date(endStr) : new Date();

        if (parsedStart && !isNaN(parsedStart.getTime())) {
          setStartDate(parsedStart);
        } else {
          setStartDate(new Date());
        }

        if (parsedEnd && !isNaN(parsedEnd.getTime())) {
          setEndDate(parsedEnd);
        } else {
          setEndDate(new Date());
        }

        const allDayVal = leave.isAllDay !== undefined ? leave.isAllDay : leave.is_all_day;
        const isAllDay = allDayVal !== undefined ? !!allDayVal : true;
        setLeaveType(isAllDay ? "days" : "hours");

        const pad = (n: number) => n.toString().padStart(2, "0");
        if (parsedStart && !isNaN(parsedStart.getTime())) {
          setStartTime(`${pad(parsedStart.getHours())}:${pad(parsedStart.getMinutes())}`);
        }
        if (parsedEnd && !isNaN(parsedEnd.getTime())) {
          setEndTime(`${pad(parsedEnd.getHours())}:${pad(parsedEnd.getMinutes())}`);
        }
        setSelectedTechIds([leave.technician]);
      } else {
        const defaultDate = defaultStartDate && !isNaN(new Date(defaultStartDate).getTime())
          ? new Date(defaultStartDate)
          : new Date();
        setStartDate(defaultDate);
        setEndDate(defaultDate);
        setLeaveType("days");
        setStartTime("00:00");
        setEndTime("23:59");
        setSelectedTechIds([]);
      }
    }
  }, [show, leave, defaultStartDate]);

  const handleCheckAll = (checked: boolean) => {
    if (checked) {
      setSelectedTechIds(technicians.map((t) => t.id));
    } else {
      setSelectedTechIds([]);
    }
  };

  const handleTechCheck = (techId: string, checked: boolean) => {
    if (checked) {
      setSelectedTechIds([...selectedTechIds, techId]);
    } else {
      setSelectedTechIds(selectedTechIds.filter((id) => id !== techId));
    }
  };

  const isCheckAllChecked =
    technicians.length > 0 && selectedTechIds.length === technicians.length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate) {
      alert("Please select at least a start date.");
      return;
    }
    if (selectedTechIds.length === 0) {
      alert("Please select at least one technician.");
      return;
    }

    setIsSubmitting(true);

    let finalStart = new Date(startDate);
    let finalEnd = leaveType === "days" && endDate ? new Date(endDate) : new Date(startDate);

    const isAllDay = leaveType === "days";

    if (isAllDay) {
      finalStart.setHours(0, 0, 0, 0);
      finalEnd.setHours(23, 59, 59, 999);
    } else {
      const [startH, startM] = startTime.split(":").map(Number);
      const [endH, endM] = endTime.split(":").map(Number);
      finalStart.setHours(startH || 0, startM || 0, 0, 0);
      finalEnd.setHours(endH || 23, endM || 59, 0, 0);
    }

    try {
      if (leave) {
        // Edit mode - update the current leave with the first selected technician
        const firstTechId = selectedTechIds[0];
        const data = {
          technician: firstTechId,
          start_date_time: finalStart.toISOString(),
          end_date_time: finalEnd.toISOString(),
          is_all_day: isAllDay,
        };
        await TechnicianService.updateLeave(leave.id, data);

        // If there are other technicians checked, bulk-create leaves for them
        const remainingTechIds = selectedTechIds.slice(1);
        if (remainingTechIds.length > 0) {
          const bulkData = {
            technician_ids: remainingTechIds,
            start_date_time: finalStart.toISOString(),
            end_date_time: finalEnd.toISOString(),
            is_all_day: isAllDay,
          };
          await TechnicianService.createLeave(bulkData);
        }
      } else {
        // Add mode - bulk create leaves
        const data = {
          technician_ids: selectedTechIds,
          start_date_time: finalStart.toISOString(),
          end_date_time: finalEnd.toISOString(),
          is_all_day: isAllDay,
        };
        await TechnicianService.createLeave(data);
      }
      onLeaveChanged();
      onHide();
    } catch (err) {
      console.error("Error saving leave", err);
      alert("Error saving technician leave. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!leave) return;
    if (!window.confirm("Are you sure you want to delete this leave?")) return;

    setIsSubmitting(true);
    try {
      await TechnicianService.deleteLeave(leave.id);
      onLeaveChanged();
      onHide();
    } catch (err) {
      console.error("Error deleting leave", err);
      alert("Error deleting leave. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>{leave ? "Edit Technician Leave" : "Add Technician Leave"}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflow: "visible" }}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Leave Type</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                id="leave-type-days"
                label="Whole days"
                name="leaveType"
                checked={leaveType === "days"}
                onChange={() => setLeaveType("days")}
              />
              <Form.Check
                type="radio"
                id="leave-type-hours"
                label="Hourly (single day)"
                name="leaveType"
                checked={leaveType === "hours"}
                onChange={() => setLeaveType("hours")}
              />
            </div>
          </Form.Group>

          {leaveType === "days" ? (
            <div className="mb-3" style={{ position: "relative" }}>
              <Form.Label className="d-block">Date Range (Start - End)</Form.Label>
              <DatePicker
                selectsRange={true}
                startDate={startDate || undefined}
                endDate={endDate || undefined}
                onChange={(update: [Date | null, Date | null]) => {
                  const [start, end] = update;
                  setStartDate(start);
                  setEndDate(end);
                }}
                isClearable={true}
                dateFormat="MMMM d, yyyy"
                className="form-control"
                placeholderText="Select date range"
              />
            </div>
          ) : (
            <>
              <div className="mb-3">
                <Form.Label className="d-block">Date</Form.Label>
                <DatePicker
                  selected={startDate}
                  onChange={(date: Date | null) => {
                    setStartDate(date);
                    setEndDate(date);
                  }}
                  dateFormat="MMMM d, yyyy"
                  className="form-control"
                  placeholderText="Select date"
                />
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <Form.Label>Start Time</Form.Label>
                  <Form.Control
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <Form.Label>End Time</Form.Label>
                  <Form.Control
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          <hr />

          {leave ? (
            <div className="mt-3 mb-2 p-2 border rounded bg-light d-flex align-items-center">
              <span className="fw-bold text-secondary">Technician:</span>{" "}
              <span className="fs-6 fw-bold text-dark ms-1">
                {leave.technicianName || leave.technician_name || "Unknown"}
              </span>
              <div className="ms-2">
                {technicians.map((t) => {
                  if (t.id === leave.technician) {
                    const supportsDyno = t.supportsDyno !== undefined ? t.supportsDyno : t.supports_dyno;
                    const supportsService = t.supportsService !== undefined ? t.supportsService : t.supports_service;
                    const badges = [];
                    if (supportsDyno !== false) {
                      badges.push(<span key="dyno" className="badge bg-info text-dark ms-1" style={{ fontSize: "10px" }}>Dyno</span>);
                    }
                    if (supportsService !== false) {
                      badges.push(<span key="service" className="badge bg-success ms-1" style={{ fontSize: "10px" }}>Service</span>);
                    }
                    return badges;
                  }
                  return null;
                })}
              </div>
            </div>
          ) : (
            <>
              <hr />
              <Form.Label className="fw-bold">Technicians</Form.Label>
              <Form.Group className="mb-2">
                <Form.Check
                  type="checkbox"
                  id="check-all-techs"
                  label="Check All"
                  checked={isCheckAllChecked}
                  onChange={(e) => handleCheckAll(e.target.checked)}
                />
              </Form.Group>

              <div style={{ maxHeight: "200px", overflowY: "auto", paddingLeft: "10px" }}>
                {technicians.map((tech) => {
                  const isChecked = selectedTechIds.includes(tech.id);
                  const supportsDyno = tech.supportsDyno !== undefined ? tech.supportsDyno : tech.supports_dyno;
                  const supportsService = tech.supportsService !== undefined ? tech.supportsService : tech.supports_service;
                  const badges = [];
                  if (supportsDyno !== false) {
                    badges.push(<span key="dyno" className="badge bg-info text-dark ms-1" style={{ fontSize: "10px" }}>Dyno</span>);
                  }
                  if (supportsService !== false) {
                    badges.push(<span key="service" className="badge bg-success ms-1" style={{ fontSize: "10px" }}>Service</span>);
                  }
                  return (
                    <Form.Group key={tech.id} className="mb-1">
                      <div className="d-flex align-items-center">
                        <Form.Check
                          type="checkbox"
                          id={`tech-${tech.id}`}
                          label={tech.name}
                          checked={isChecked}
                          onChange={(e) => {
                            handleTechCheck(tech.id, e.target.checked);
                          }}
                        />
                        <div className="ms-1">
                          {badges}
                        </div>
                      </div>
                    </Form.Group>
                  );
                })}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {leave && (
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={isSubmitting}
              className="me-auto"
            >
              Delete
            </Button>
          )}
          <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default LeaveModal;
